import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import {
  Loader2,
  ArrowUpRight,
  UploadCloud,
  CheckCircle2,
  Sparkles,
  X,
  Linkedin,
  Github,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { parseCv, submitCandidate, getJob, listJobs } from "@/lib/api";
import { useCandidate } from "@/lib/candidate";
import { rankJobs, matchBadge } from "@/lib/jobMatch";
import { APPLY, CV } from "@/constants/testIds";

const EASE = [0.76, 0, 0.24, 1];
const MAX_MB = 4;

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  headline: "",
  current_role: "",
  current_company: "",
  years_of_experience: "",
  linkedin_url: "",
  github_url: "",
  skills: [],
  summary: "",
};

const formatDetail = (d) => {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" · ");
  return String(d);
};

export default function ApplyPage() {
  const [params] = useSearchParams();
  const jobId = params.get("job");
  const navigate = useNavigate();
  const { parsedCV, setParsedCV, cvFilename, setCvFilename, cvText, setCvText, clear } =
    useCandidate();

  const [form, setForm] = useState(emptyForm);
  const [job, setJob] = useState(null);
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | parsing | done | error
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [skillInput, setSkillInput] = useState("");
  const [topMatches, setTopMatches] = useState([]);
  const [cvStoragePath, setCvStoragePath] = useState(null);
  const inputRef = useRef(null);

  // Hydrate from context (if user came from CV modal)
  useEffect(() => {
    if (parsedCV) {
      setForm((f) => ({
        ...f,
        name: parsedCV.name || f.name,
        email: parsedCV.email || f.email,
        phone: parsedCV.phone || f.phone,
        headline: parsedCV.headline || f.headline,
        current_role: parsedCV.current_role || f.current_role,
        current_company: parsedCV.current_company || f.current_company,
        years_of_experience:
          parsedCV.years_of_experience != null
            ? String(parsedCV.years_of_experience)
            : f.years_of_experience,
        linkedin_url: parsedCV.linkedin_url || f.linkedin_url,
        github_url: parsedCV.github_url || f.github_url,
        skills:
          parsedCV.skills && parsedCV.skills.length > 0 ? parsedCV.skills : f.skills,
        summary: parsedCV.summary || f.summary,
      }));
      if (parsedCV.cv_storage_path) setCvStoragePath(parsedCV.cv_storage_path);
      setStatus("done");
    }
  }, []);

  useEffect(() => {
    if (jobId) {
      getJob(jobId).then(setJob).catch(() => setJob(null));
    }
  }, [jobId]);

  // Live "top matches" strip — recomputes whenever the candidate's skills change
  useEffect(() => {
    if (!form.skills?.length) {
      setTopMatches([]);
      return;
    }
    let cancelled = false;
    listJobs({})
      .then((all) => {
        if (cancelled) return;
        const ranked = rankJobs(all, form.skills).filter((j) => (j._match?.score ?? 0) > 0);
        setTopMatches(ranked.slice(0, 3));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [form.skills]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addSkill = (s) => {
    const v = s.trim();
    if (!v) return;
    setForm((f) =>
      f.skills.includes(v) ? f : { ...f, skills: [...f.skills, v].slice(0, 30) }
    );
  };

  const removeSkill = (s) => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }));

  const onSkillKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addSkill(skillInput);
      setSkillInput("");
    } else if (e.key === "Backspace" && !skillInput && form.skills.length) {
      removeSkill(form.skills[form.skills.length - 1]);
    }
  };

  const handleFile = useCallback(
    async (f) => {
      if (!f) return;
      const nm = f.name.toLowerCase();
      if (!(nm.endsWith(".pdf") || nm.endsWith(".docx") || nm.endsWith(".txt"))) {
        setStatus("error");
        setError("Please upload a PDF, DOCX or TXT file.");
        return;
      }
      if (f.size > MAX_MB * 1024 * 1024) {
        setStatus("error");
        setError(`File is larger than ${MAX_MB} MB.`);
        return;
      }
      setFile(f);
      setError("");
      setStatus("parsing");
      try {
        const parsed = await parseCv(f);
        setParsedCV(parsed);
        setCvFilename(parsed.cv_filename || f.name);
        setCvText(parsed.cv_text_preview || null);
        setCvStoragePath(parsed.cv_storage_path || null);
        setForm((old) => ({
          ...old,
          name: parsed.name || old.name,
          email: parsed.email || old.email,
          phone: parsed.phone || old.phone,
          headline: parsed.headline || old.headline,
          current_role: parsed.current_role || old.current_role,
          current_company: parsed.current_company || old.current_company,
          years_of_experience:
            parsed.years_of_experience != null
              ? String(parsed.years_of_experience)
              : old.years_of_experience,
          linkedin_url: parsed.linkedin_url || old.linkedin_url,
          github_url: parsed.github_url || old.github_url,
          skills:
            parsed.skills && parsed.skills.length > 0 ? parsed.skills : old.skills,
          summary: parsed.summary || old.summary,
        }));
        setStatus("done");
        toast.success("CV parsed — review and confirm.");
      } catch (err) {
        setStatus("error");
        setError(formatDetail(err?.response?.data?.detail) || err.message);
      }
    },
    [setParsedCV, setCvFilename, setCvText]
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const yrs = form.years_of_experience === "" ? null : Number(form.years_of_experience);
      await submitCandidate({
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        headline: form.headline || null,
        current_role: form.current_role || null,
        current_company: form.current_company || null,
        years_of_experience: Number.isFinite(yrs) ? yrs : null,
        linkedin_url: form.linkedin_url || null,
        github_url: form.github_url || null,
        skills: form.skills,
        summary: form.summary || null,
        job_id: jobId || null,
        cv_filename: cvFilename || file?.name || null,
        cv_storage_path: cvStoragePath || null,
        cv_text: cvText || null,
      });
      setSuccess(true);
      toast.success("Application received — we'll be in touch.");
      clear();
      setTimeout(() => navigate("/jobs"), 2000);
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[var(--bg)] min-h-screen pt-40 pb-24">
      <Seo
        title="Apply — Candidate profile & CV upload"
        description="Drop your CV once. Our AI extracts your skills, LinkedIn and GitHub in seconds so you can apply to matching roles in a single click."
        path="/apply"
        noIndex
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
        {/* Left column */}
        <div className="md:col-span-4">
          <p className="overline mb-6">Candidate · Apply</p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-display text-5xl md:text-6xl tracking-[-0.03em] leading-[0.92] text-[var(--ink)]"
          >
            Your{" "}
            <span className="font-serif-accent italic text-[var(--accent)]">
              profile
            </span>
            .
          </motion.h1>

          {job && (
            <div
              data-testid={APPLY.jobBadge}
              className="mt-8 border border-[var(--accent)]/50 bg-[var(--bg-2)] p-5"
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--accent)] mb-2">
                Applying to
              </p>
              <p className="font-display text-lg text-[var(--ink)] leading-tight mb-1">
                {job.title}
              </p>
              <p className="text-sm text-[var(--ink-70)]">
                {job.company} · {job.location}
              </p>
            </div>
          )}

          {/* CV drop */}
          <div className="mt-8">
            <p className="overline mb-3">Upload CV — auto-fill profile</p>
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                handleFile(e.dataTransfer?.files?.[0]);
              }}
              className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
                dragging
                  ? "border-[var(--accent)] bg-[var(--bg-2)]"
                  : "border-[var(--line-strong)] hover:bg-[var(--bg-2)]"
              }`}
              data-testid={APPLY.cvDrop}
            >
              {status === "parsing" ? (
                <>
                  <Loader2 className="w-6 h-6 text-[var(--accent)] mx-auto mb-2 animate-spin" />
                  <p className="text-sm text-[var(--ink-70)]">Reading {file?.name}…</p>
                </>
              ) : status === "done" && (cvFilename || file) ? (
                <>
                  <FileText className="w-6 h-6 text-[var(--accent)] mx-auto mb-2" />
                  <p className="text-sm text-[var(--ink)] truncate">
                    {cvFilename || file?.name}
                  </p>
                  <p className="text-xs text-[var(--ink-50)] mt-1">
                    Click to replace
                  </p>
                </>
              ) : (
                <>
                  <UploadCloud className="w-6 h-6 text-[var(--accent)] mx-auto mb-2" />
                  <p className="text-sm text-[var(--ink)]">
                    Drop CV or click to upload
                  </p>
                  <p className="text-xs text-[var(--ink-50)] mt-1">
                    PDF / DOCX / TXT · under {MAX_MB} MB
                  </p>
                </>
              )}
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid={CV.fileInput + "-apply"}
              />
            </div>
            {status === "error" && error && (
              <p className="text-red-600 text-xs mt-3">{error}</p>
            )}
            {parsedCV && status === "done" && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-[var(--accent)]">
                <Sparkles className="w-3.5 h-3.5" />
                Profile auto-filled from CV
              </p>
            )}
          </div>

          {/* Top matches strip */}
          {topMatches.length > 0 && (
            <div className="mt-10 border-t border-[var(--line-strong)] pt-8" data-testid="apply-top-matches">
              <p className="overline mb-4">Your top matches</p>
              <div className="space-y-3">
                {topMatches.map((m) => {
                  const badge = matchBadge(m._match.score);
                  return (
                    <Link
                      key={m.id}
                      to={`/apply?job=${m.id}`}
                      className="block border border-[var(--line-strong)] hover:border-[var(--accent)] p-4 transition-colors"
                      data-testid={`apply-top-match-${m.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-display text-sm text-[var(--ink)] leading-tight truncate">
                            {m.title}
                          </p>
                          <p className="text-xs text-[var(--ink-70)] mt-1 truncate">
                            {m.company} · {m.location}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] uppercase tracking-[0.22em] px-2 py-1 border shrink-0 ${
                            badge.tone === "strong"
                              ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                              : "border-[var(--line-strong)] text-[var(--ink)]"
                          }`}
                        >
                          {m._match.score}%
                        </span>
                      </div>
                    </Link>
                  );
                })}
                <Link
                  to="/jobs"
                  className="link-slide inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[var(--ink)] mt-2"
                >
                  <span>See all matches</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Right — form */}
        <form
          onSubmit={onSubmit}
          className="md:col-span-8 space-y-2"
          data-testid={APPLY.form}
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="overline block mb-2">Full name</label>
              <input
                required
                className="editorial-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Full name"
                data-testid={APPLY.name}
              />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                required
                type="email"
                className="editorial-input"
                value={form.email}
                onChange={set("email")}
                placeholder="you@company.com"
                data-testid={APPLY.email}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-4">
            <div>
              <label className="overline block mb-2">Phone</label>
              <input
                className="editorial-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 …"
                data-testid={APPLY.phone}
              />
            </div>
            <div>
              <label className="overline block mb-2">Years of experience</label>
              <input
                type="number"
                min="0"
                max="60"
                className="editorial-input"
                value={form.years_of_experience}
                onChange={set("years_of_experience")}
                placeholder="e.g. 7"
                data-testid={APPLY.years}
              />
            </div>
          </div>

          <div className="pt-4">
            <label className="overline block mb-2">Headline</label>
            <input
              className="editorial-input"
              value={form.headline}
              onChange={set("headline")}
              placeholder="e.g. Senior Backend Engineer · Python & AWS"
              data-testid={APPLY.headline}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-4">
            <div>
              <label className="overline block mb-2">Current role</label>
              <input
                className="editorial-input"
                value={form.current_role}
                onChange={set("current_role")}
                placeholder="Senior Software Engineer"
                data-testid={APPLY.role}
              />
            </div>
            <div>
              <label className="overline block mb-2">Company</label>
              <input
                className="editorial-input"
                value={form.current_company}
                onChange={set("current_company")}
                placeholder="Employer"
                data-testid={APPLY.company}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 pt-4">
            <div>
              <label className="overline mb-2 flex items-center gap-2">
                <Linkedin className="w-3.5 h-3.5" /> LinkedIn
              </label>
              <input
                className="editorial-input"
                value={form.linkedin_url}
                onChange={set("linkedin_url")}
                placeholder="https://linkedin.com/in/…"
                data-testid={APPLY.linkedin}
              />
            </div>
            <div>
              <label className="overline mb-2 flex items-center gap-2">
                <Github className="w-3.5 h-3.5" /> GitHub
              </label>
              <input
                className="editorial-input"
                value={form.github_url}
                onChange={set("github_url")}
                placeholder="https://github.com/…"
                data-testid={APPLY.github}
              />
            </div>
          </div>

          {/* Skills */}
          <div className="pt-4">
            <label className="overline block mb-2">Skills · type + Enter</label>
            <div
              className="border-b border-[var(--line-strong)] py-2 flex flex-wrap items-center gap-2"
              data-testid={APPLY.skills}
            >
              {form.skills.map((s) => (
                <span
                  key={s}
                  className="text-xs text-[var(--ink)] border border-[var(--line-strong)] rounded-full pl-2.5 pr-1 py-0.5 inline-flex items-center gap-1"
                >
                  {s}
                  <button
                    type="button"
                    onClick={() => removeSkill(s)}
                    aria-label={`Remove ${s}`}
                    data-testid={APPLY.skillRemove(s.replace(/[^a-z0-9]+/gi, "-").toLowerCase())}
                    className="ml-1 text-[var(--ink-50)] hover:text-[var(--accent)] p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                className="flex-1 min-w-[120px] bg-transparent outline-none py-1 text-sm"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={onSkillKey}
                onBlur={() => {
                  if (skillInput.trim()) {
                    addSkill(skillInput);
                    setSkillInput("");
                  }
                }}
                placeholder={form.skills.length ? "Add another…" : "React, Python, AWS…"}
                data-testid={APPLY.skillsInput}
              />
            </div>
          </div>

          <div className="pt-4">
            <label className="overline block mb-2">Professional summary</label>
            <textarea
              rows={4}
              className="editorial-input resize-none"
              value={form.summary}
              onChange={set("summary")}
              placeholder="A short summary of your experience & interests…"
              data-testid={APPLY.summary}
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm pt-4" data-testid={APPLY.error}>
              {error}
            </div>
          )}

          <div className="pt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <button
              type="submit"
              disabled={submitting || success}
              className="btn-pill disabled:opacity-60"
              data-testid={APPLY.submit}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending…</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Sent</span>
                </>
              ) : (
                <>
                  <span>Submit application</span>
                  <ArrowUpRight className="w-4 h-4" />
                </>
              )}
            </button>
            {success && (
              <div
                className="flex items-center gap-2 text-[var(--accent)]"
                data-testid={APPLY.success}
              >
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm uppercase tracking-[0.22em]">
                  Application received
                </span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
